"use client";

import { useEffect, useState } from "react";
import type {
    ComponentPropsWithoutRef,
    MouseEvent,
} from "react";
import {
    siteBuilderShareTarget,
    siteBuilderShareTargetOpensNewWindow,
} from "./siteBuilderShare";
import type {
    SiteBuilderShareData,
    SiteBuilderShareNetwork,
} from "./siteBuilderShare";

type SiteBuilderShareAnchorProps = Omit<
    ComponentPropsWithoutRef<"a">,
    "href"
> & {
    editorMode: boolean;
    explicitHref?: string | null;
    locationHref?: string;
    network: SiteBuilderShareNetwork;
    openInNewWindow?: boolean;
    shareTitle?: string;
};

/**
 * Small client boundary used by every renderer.  It reads the canonical page
 * URL only after hydration, which keeps server rendering deterministic while
 * making default share icons functional on the public page.
 */
export function SiteBuilderShareAnchor({
    draggable,
    editorMode,
    explicitHref,
    locationHref,
    network,
    onClick,
    openInNewWindow,
    rel,
    shareTitle = "",
    target,
    ...anchorProps
}: SiteBuilderShareAnchorProps) {
    const [shareData, setShareData] = useState<SiteBuilderShareData>({
        title: shareTitle,
        url: "",
    });

    useEffect(() => {
        let url = window.location.href;

        if (locationHref) {
            try {
                url = new URL(locationHref, "https://shop.preview").href;
            } catch {
                // Keep the real browser URL when an authored preview URL is malformed.
            }
        }

        setShareData({
            title: document.title.trim() || shareTitle,
            url,
        });
    }, [locationHref, shareTitle]);

    const href = siteBuilderShareTarget(network, explicitHref, shareData);
    const interactiveHref = editorMode ? "" : href;
    const opensNewWindow = openInNewWindow
        ?? siteBuilderShareTargetOpensNewWindow(interactiveHref);

    const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
        if (editorMode || href === "") {
            event.preventDefault();
        }

        onClick?.(event);
    };

    return (
        <a
            {...anchorProps}
            draggable={editorMode ? false : draggable}
            href={interactiveHref || undefined}
            onClick={handleClick}
            rel={rel ?? (opensNewWindow ? "noopener noreferrer" : undefined)}
            target={target ?? (opensNewWindow ? "_blank" : undefined)}
        />
    );
}
